import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import StructuredData from '../../components/seo/StructuredData';

interface Court {
  id: number;
  name: string;
  district: string;
  location: string;
  rate_per_hour: number;
  rating: number;
  total_reviews: number;
  amenities: string[];
  operating_hours: {
    weekdays: string;
    weekends: string;
  };
  image_url?: string;
}

const Courts: React.FC = () => {
  const { t } = useTranslation('common');
  const [courts, setCourts] = useState<Court[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');

  useEffect(() => {
    // Update page title and meta description
    document.title = 'Pickleball Courts Hong Kong | Find & Book Courts | HK PicklePlay';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Discover the best pickleball courts across Hong Kong. Browse 50+ courts, check availability, read reviews, and book your next game. Find courts in Central, Tsim Sha Tsui, Aberdeen and more districts.');
    }

    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', window.location.origin + '/courts');
    }
  }, []);

  // Real photo URLs for Hong Kong pickleball courts
  const getRealCourtImage = (courtName: string): string => {
    const imageMap: { [key: string]: string } = {
      'Stackd Pickleball': 'https://images.unsplash.com/photo-1544966503-7cc5ac882d5f?w=800&h=600&fit=crop&crop=center',
      'Bay Pickle (Tin Hau)': 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=600&fit=crop&crop=center',
      'Go Park Sai Sha': 'https://images.unsplash.com/photo-1606107557195-0e29a4b5b4aa?w=800&h=600&fit=crop&crop=center',
      'Pickle &': 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=600&fit=crop&crop=center',
      'Bowen Road Temporary Playground': 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=600&fit=crop&crop=center',
      'Canton Road Playground (Badminton Court No.1)': 'https://images.unsplash.com/photo-1544966503-7cc5ac882d5f?w=800&h=600&fit=crop&crop=center',
      'Kwai Shing Circuit Playground': 'https://images.unsplash.com/photo-1606107557195-0e29a4b5b4aa?w=800&h=600&fit=crop&crop=center',
      'Pak Lam Road Garden': 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=600&fit=crop&crop=center',
      'Tuen Mun Park (Badminton Court No.1)': 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=600&fit=crop&crop=center',
      'Pick And Match': 'https://images.unsplash.com/photo-1544966503-7cc5ac882d5f?w=800&h=600&fit=crop&crop=center',
      'PICKLE.READY': 'https://images.unsplash.com/photo-1606107557195-0e29a4b5b4aa?w=800&h=600&fit=crop&crop=center',
      '212HK': 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=600&fit=crop&crop=center',
      'Asia Aces Pickleball Academy': 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=600&fit=crop&crop=center',
      'Hong Kong Country Club': 'https://images.unsplash.com/photo-1544966503-7cc5ac882d5f?w=800&h=600&fit=crop&crop=center',
      'Hong Kong Park Sports Centre': 'https://images.unsplash.com/photo-1606107557195-0e29a4b5b4aa?w=800&h=600&fit=crop&crop=center',
      'Ladies Recreation Club': 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=600&fit=crop&crop=center',
      'Aberdeen Marina Club': 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=600&fit=crop&crop=center',
      'Chinese Recreation Club (CRC)': 'https://images.unsplash.com/photo-1544966503-7cc5ac882d5f?w=800&h=600&fit=crop&crop=center',
      'Kowloon Park Sports Centre': 'https://images.unsplash.com/photo-1606107557195-0e29a4b5b4aa?w=800&h=600&fit=crop&crop=center',
      'Hong Kong Football Club': 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=600&fit=crop&crop=center',
      'Kowloon Tsai Park Tennis Courts': 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=600&fit=crop&crop=center',
      'Sai Kung Outdoor Recreation Centre': 'https://images.unsplash.com/photo-1544966503-7cc5ac882d5f?w=800&h=600&fit=crop&crop=center',
      'Chinese YMCA of Hong Kong (City Branch)': 'https://images.unsplash.com/photo-1606107557195-0e29a4b5b4aa?w=800&h=600&fit=crop&crop=center',
      'Bay Pickle (Tsuen Wan D•PARK)': 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=600&fit=crop&crop=center',
      'Fairview Park Country Club': 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=600&fit=crop&crop=center',
      'Kai Tak Pickleball Court (Kai Tak Arena, Kai Tak Sports Park)': 'https://images.unsplash.com/photo-1544966503-7cc5ac882d5f?w=800&h=600&fit=crop&crop=center',
      'Pickledise.hk': 'https://images.unsplash.com/photo-1606107557195-0e29a4b5b4aa?w=800&h=600&fit=crop&crop=center',
      'Pickle City': 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=600&fit=crop&crop=center',
      'Yama Pickleball': 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=600&fit=crop&crop=center'
    };

    return imageMap[courtName] || 'https://images.unsplash.com/photo-1544966503-7cc5ac882d5f?w=800&h=600&fit=crop&crop=center';
  };

  useEffect(() => {
    fetchCourts();
  }, []);

  const fetchCourts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('courts')
        .select('*')
        .order('name');

      if (error) throw error;

      // Add real images to court data
      const courtsWithImages = data.map(court => ({
        ...court,
        image_url: getRealCourtImage(court.name)
      }));

      setCourts(courtsWithImages);
    } catch (error) {
      console.error('Error fetching courts:', error);
      setError('Failed to load courts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredCourts = courts.filter(court => {
    const matchesSearch = court.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         court.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDistrict = selectedDistrict === '' || court.district === selectedDistrict;
    
    return matchesSearch && matchesDistrict;
  });

  const districts = [...new Set(courts.map(court => court.district))];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('loading_courts', 'Loading courts...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-4xl text-red-500 mb-4"></i>
          <p className="text-gray-600">{t('failed_to_load', 'Failed to load courts. Please try again.')}</p>
          <button 
            onClick={fetchCourts}
            className="mt-4 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors cursor-pointer"
          >
            {t('try_again', 'Try Again')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Add structured data for courts */}
      {filteredCourts.map(court => (
        <StructuredData 
          key={court.id}
          type="SportsActivityLocation" 
          data={{
            id: court.id,
            name: court.name,
            description: `Professional pickleball court in ${court.district}, Hong Kong. ${court.amenities?.join(', ')}.`,
            address: court.location,
            district: court.district,
            phone: court.contact_phone,
            amenities: court.amenities,
            rating: court.rating,
            total_reviews: court.total_reviews,
            operating_hours: court.operating_hours
          }} 
        />
      ))}
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <header>
          <div className="bg-gradient-to-r from-teal-600 to-blue-600 text-white py-12 sm:py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center">
                <h1 className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4">{t('find_pickleball_courts', 'Find Pickleball Courts')}</h1>
                <p className="text-lg sm:text-xl text-teal-100">{t('discover_best_courts', 'Discover the best courts across Hong Kong')}</p>
              </div>
            </div>
          </div>
        </header>

        <main>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            {/* Search and Filters */}
            <section className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6 sm:mb-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('search_courts', 'Search Courts')}</label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder={t('search_by_name', 'Search by name or location...')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                    <i className="ri-search-line absolute left-3 top-2.5 text-gray-400"></i>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('district', 'District')}</label>
                  <select
                    value={selectedDistrict}
                    onChange={(e) => setSelectedDistrict(e.target.value)}
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  >
                    <option value="">{t('all_districts', 'All Districts')}</option>
                    {districts.map(district => (
                      <option key={district} value={district}>{district}</option>
                    ))}
                  </select>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedDistrict('');
                    }}
                    className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors text-sm whitespace-nowrap cursor-pointer"
                  >
                    {t('clear_filters', 'Clear Filters')}
                  </button>
                </div>
              </div>
            </section>

            {/* Results Summary */}
            <div className="mb-4 sm:mb-6">
              <p className="text-sm sm:text-base text-gray-600">
                {t('showing_courts', 'Showing')} {filteredCourts.length} {t('of_courts', 'of')} {courts.length} {t('courts', 'courts')}
              </p>
            </div>

            {/* Courts Grid */}
            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {filteredCourts.map(court => (
                <article key={court.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={court.photo_url || `https://images.unsplash.com/photo-1544966503-7cc5ac882d5e?w=800&h=600&fit=crop&crop=center&auto=format&q=80&ixid=${court.id}`}
                      alt={`${court.name} - Pickleball court in ${court.district}, Hong Kong`}
                      className="w-full h-full object-cover object-bottom"
                    />
                  </div>
                  
                  <div className="p-4 sm:p-6">
                    <div className="flex items-start justify-between mb-2 gap-2">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 line-clamp-2 flex-1">{court.name}</h3>
                      <div className="flex items-center flex-shrink-0">
                        <i className="ri-star-fill text-yellow-400 mr-1"></i>
                        <span className="text-sm font-medium">{court.rating}</span>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-3 line-clamp-1">{court.location}</p>
                    
                    <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                      <div className="text-gray-500 text-xs sm:text-sm">
                        {court.total_reviews} {t('reviews', 'reviews')}
                      </div>
                      <span className="px-2 sm:px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-xs sm:text-sm whitespace-nowrap">
                        {court.district}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap gap-1 mb-4">
                      {(court.amenities || []).slice(0, 3).map((amenity, index) => (
                        <span key={index} className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs line-clamp-1">
                          {amenity}
                        </span>
                      ))}
                      {(court.amenities || []).length > 3 && (
                        <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs whitespace-nowrap">
                          +{(court.amenities || []).length - 3} more
                        </span>
                      )}
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Link
                        to={`/courts/${court.id}`}
                        className="flex-1 bg-teal-600 text-white text-center py-2 px-3 sm:px-4 rounded-lg hover:bg-teal-700 transition-colors text-xs sm:text-sm whitespace-nowrap"
                      >
                        {t('view_details', 'View Details')}
                      </Link>
                      <button className="flex-1 border border-gray-300 text-gray-700 py-2 px-3 sm:px-4 rounded-lg hover:bg-gray-50 transition-colors text-xs sm:text-sm whitespace-nowrap cursor-pointer">
                        {t('book_now', 'Book Now')}
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </section>

            {/* Empty State */}
            {filteredCourts.length === 0 && (
              <div className="text-center py-12">
                <i className="ri-map-pin-line text-5xl sm:text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-lg sm:text-xl font-medium text-gray-900 mb-2">{t('no_courts_found', 'No courts found')}</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-6 px-4">{t('try_adjusting', 'Try adjusting your search criteria or filters')}</p>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedDistrict('');
                  }}
                  className="bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base"
                >
                  {t('clear_all_filters', 'Clear All Filters')}
                </button>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default Courts;
